import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import { Link } from 'react-router-dom';

import 'swiper/css';
import 'swiper/css/pagination';

// Keyframes for shading animation
const shadeAnimation = keyframes`
  0% {
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2);
  }
  50% {
    text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.5);
  }
  100% {
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2);
  }
`;

const SliderContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
`;

const StyledSwiper = styled(Swiper)`
  width: 100%;
`;

const SlideContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 20px;
  background: linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%);
  color: #fff;
  text-align: left;
`;

const SlideSubtitle = styled.h2`
  position: relative;
  font-size: 18px;
  margin-bottom: 5px;
  color: #4CAF50;
  text-transform: uppercase;
  font-weight: bold;
  animation: ${shadeAnimation} 2s ease-in-out infinite;
  letter-spacing: 1px;

  // Create a projection effect using a ::before pseudo-element
  &::before {
    content: attr(data-text); // Use the same text as the main element
    position: absolute;
    top: 3px;   // Offset vertically
    left: 3px;  // Offset horizontally
    color: rgba(0, 0, 0, 0.3); // Shadow color for the projection
    z-index: -1; // Ensure it’s behind the actual text
  }
`;

const SlideTitle = styled.h1`
  position: relative;
  font-size: 36px;
  margin-bottom: 15px;
  color: #FF0000;
  font-weight: bold;
  animation: ${shadeAnimation} 2s ease-in-out infinite;
  letter-spacing: 1px;

  @media (max-width: 768px) {
    font-size: 24px;
  }

  // Create a projection effect using a ::before pseudo-element
  &::before {
    content: attr(data-text); // Use the same text as the main element
    position: absolute;
    top: 5px;   // Offset vertically
    left: 5px;  // Offset horizontally
    color: rgba(0, 0, 0, 0.4); // Shadow color for the projection
    z-index: -1; // Ensure it’s behind the actual text
  }
`;

const ShopNowButton = styled(Link)`
  display: inline-block;
  padding: 10px 20px;
  background-color: #4CAF50;
  color: #fff;
  text-decoration: none;
  font-size: 16px;
  border-radius: 5px;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #45a049;
  }
`;

const SlideImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const MainSlider = () => {
  const [slides, setSlides] = useState([]);

  useEffect(() => {
    const fetchSliderData = async () => {
      try {
        const response = await fetch('/api/sliders/');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setSlides(data);
      } catch (error) {
        console.error('Error fetching slider data:', error);
      }
    };

    fetchSliderData();
  }, []);

  const getSlideLink = (slide) => {
    if (slide.button_url) {
      const url = new URL(slide.button_url, window.location.origin);
      const path = url.pathname;
      
      if (path.startsWith('/api/categories/')) {
        return `/categories${path.slice('/api/categories'.length)}`;
      }
      
      if (path.startsWith('/api/products/')) {
        return `/product${path.slice('/api/products'.length)}`;
      }
      
      return path.startsWith('/api') ? '/' : path;
    }
    return '/';
  };

  return (
    <SliderContainer>
      <StyledSwiper
        modules={[Pagination]}
        pagination={{
          clickable: true,
        }}
        spaceBetween={0}
        slidesPerView={1}
      >
        {slides.map((slide, index) => (
          <SwiperSlide key={index}>
            <SlideImage src={slide.image} alt={slide.title} />
            <SlideContent>
              <SlideSubtitle>{slide.subtitle}</SlideSubtitle>
              <SlideTitle>{slide.title}</SlideTitle>
              <ShopNowButton to={getSlideLink(slide)}>
                {slide.button_text}
              </ShopNowButton>
            </SlideContent>
          </SwiperSlide>
        ))}
      </StyledSwiper>
    </SliderContainer>
  );
};

export default MainSlider;